import { ComponentProps, useState } from 'react';
import { Chip, ChipsInput } from '../ChipsInput';
import { SelectFactory, SelectFactoryProps, Option } from '../Select/Select';

export type ChipsSelectProps<T extends string | number> = {
  value: T[];
  options: Option<T>[];
  onChange: (value: T[]) => void;
} & Pick<
  SelectFactoryProps<Option<T>>,
  'itemRenderer' | 'notFoundRenderer' | 'theme' | 'onMenuScroll' | 'externalState'
> &
  Pick<
    ComponentProps<typeof ChipsInput>,
    'placeholder' | 'error' | 'chipRenderer' | 'externalInputState' | 'limit'
  >;

export const ChipsSelect = <T extends string | number>({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  theme = 'light',
  error = false,
  itemRenderer,
  notFoundRenderer,
  chipRenderer,
  onMenuScroll,
  externalState,
  externalInputState,
  limit = 5,
}: ChipsSelectProps<T>) => {
  const internalSearchState = useState('');
  const inputState = externalInputState ?? internalSearchState;
  // Convert array to single value for SelectFactory
  const handleSelectChange = (newValue: T | null) => {
    if (newValue && !value.includes(newValue)) {
      onChange([...value, newValue]);
    }
  };

  // Convert selected values to chips format
  const selectedChips: Chip<string>[] = value.map((val) => ({
    value: String(val),
    isValid: true,
  }));

  const handleChipsChange = (chips: Chip<string>[]) => {
    const newValues = chips
      .filter((chip) => chip.isValid)
      .map((chip) => {
        const option = options.find((opt) => String(opt.value) === chip.value);
        if (option) return option.value;

        const numValue = Number(chip.value);
        return (isNaN(numValue) ? chip.value : numValue) as T;
      })
      .filter((v): v is T => v !== undefined);

    onChange(newValues);
  };

  const validateChip = (chip: string): chip is string => {
    const isValid = value.some((v) => String(v) === chip);
    return isValid;
  };

  return (
    <SelectFactory
      value={null}
      compensateOffset={22}
      onChange={handleSelectChange}
      options={options}
      inputField={(props) => (
        <ChipsInput
          {...props}
          limit={limit}
          value={selectedChips}
          onChange={handleChipsChange}
          externalInputState={inputState}
          placeholder={placeholder}
          error={error}
          validateChip={validateChip}
          chipRenderer={chipRenderer}
          splitter={[]}
        />
      )}
      itemRenderer={itemRenderer}
      notFoundRenderer={notFoundRenderer}
      theme={theme}
      nullable={false}
      noSearch
      onMenuScroll={onMenuScroll}
      externalState={externalState}
    />
  );
};
